import type { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";

import { getFromLocalStorage, removeFromLocalStorage, showErrorToast } from "src/lib/utils";

/** Errors the API rejects with carry the server's message when it sent one. */
interface ApiErrorBody {
	message?: string;
}

const NETWORK_ERROR_CODES = ["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"];

/** Rejection value: the server's message when there is one, otherwise the raw response. */
const rejectionOf = (error: AxiosError<ApiErrorBody>) => error.response?.data?.message || error.response;

export const onReqFulfilled = (config: InternalAxiosRequestConfig) => {
	const access_token = getFromLocalStorage("access_token");

	if (access_token) {
		config.headers["Authorization"] = `Bearer ${access_token}`;
	}

	return config;
};

export const onReqRejected = (error: unknown) => {
	if (process.env.NODE_ENV === "development") {
		console.error(error);
	}

	return Promise.reject(error);
};

/**
 * Unwraps every response to its payload. This is why no endpoint in `src/api/*` resolves
 * to an `AxiosResponse` — see the `api` wrapper in `./index`, which types that away.
 */
export const onResFulfilled = (response: AxiosResponse) => response.data;

export const onResRejected = (error: AxiosError<ApiErrorBody>) => {
	if (process.env.NODE_ENV === "development") {
		console.error(error);
	}

	if (error.code && NETWORK_ERROR_CODES.includes(error.code)) {
		showErrorToast("Network error", {
			description: "Please check your connection and try again",
			closeButton: true,
		});

		return Promise.reject(rejectionOf(error));
	}

	if (error.response?.status === 401) {
		removeFromLocalStorage("access_token", "user", "selectedBusiness", "selectedChild");

		window.location.reload();

		showErrorToast("Session expired", {
			description: "Please login again",
			closeButton: true,
		});

		return Promise.reject(rejectionOf(error));
	}

	if (error.response?.status === 403) {
		showErrorToast(error.response?.data?.message || "Permission denied", { closeButton: true });

		return Promise.reject(rejectionOf(error));
	}

	showErrorToast(error.response?.data?.message || "Something went wrong", { closeButton: true });

	return Promise.reject(rejectionOf(error));
};
